import * as THREE from 'three';
import { SpringChain } from './SpringChain';
import type { DogBody } from './Body';
import type { FootPose } from './Gait';
import type { DogProfile } from './Profile';

/**
 * The blocky stand-in body: capsules on pivots. Kept as the instant fallback while the
 * rigged model loads, and as the reference the rigged body has to beat.
 */
const EYE = 0x2a1a0e;
const HIP_Y = 0.45; // leg pivot height when standing
const LEG_LEN = 0.45; // leg mesh height at scale 1 (radius*2 + length)
const TORSO_Y = 0.45; // torso pivot height when standing (roughly the hips)

/** Per-leg pose: pivot height, pivot pitch (rad, + swings foot back), leg length scale. */
interface LegPose {
  y: number;
  rot: number;
  k: number;
}
// index: FL, FR, BL, BR
const SIT_LEGS: LegPose[] = [
  { y: 0.42, rot: -0.15, k: 0.95 },
  { y: 0.42, rot: -0.15, k: 0.95 },
  { y: 0.13, rot: -1.35, k: 0.55 },
  { y: 0.13, rot: -1.35, k: 0.55 },
];
const LIE_LEGS: LegPose[] = [
  { y: 0.12, rot: -1.45, k: 0.85 },
  { y: 0.12, rot: -1.45, k: 0.85 },
  { y: 0.11, rot: -1.35, k: 0.55 },
  { y: 0.11, rot: -1.35, k: 0.55 },
];
const SIT_TORSO = { y: 0.2, pitch: -0.6 };
const LIE_TORSO = { y: 0.17, pitch: -0.05 };

export class PrimitiveBody implements DogBody {
  readonly root = new THREE.Group();
  readonly neckParent = new THREE.Group(); // torso
  readonly neckPos = new THREE.Vector3(0, 0.15, 0.57);
  readonly mouth = new THREE.Object3D();
  readonly hat = new THREE.Group();
  readonly collar = new THREE.Group();
  readonly showsCollar = false;
  readonly collarRadius = 0.2;
  readonly ears: SpringChain[] = [];
  readonly tail: SpringChain;

  private body: THREE.Mesh;
  private neck = new THREE.Group();
  private legPivots: THREE.Group[] = [];
  private legMeshes: THREE.Mesh[] = [];
  private eyeGroups: THREE.Group[] = [];
  private skin: THREE.MeshStandardMaterial;
  private coatBase = new THREE.Color();

  constructor(
    private scene: THREE.Object3D,
    profile: DogProfile,
  ) {
    const skin = new THREE.MeshStandardMaterial({ color: profile.coat, roughness: 0.9 });
    this.skin = skin;
    this.coatBase.set(profile.coat);
    const dark = new THREE.MeshStandardMaterial({ color: profile.mask, roughness: 0.9 });
    const nose = new THREE.MeshStandardMaterial({ color: 0x141010, roughness: 0.35 });
    this.root.scale.setScalar(profile.size);
    const wet = new THREE.MeshStandardMaterial({ color: EYE, roughness: 0.15 });
    const pupilMat = new THREE.MeshStandardMaterial({ color: 0x000000, roughness: 0.3 });

    const torso = this.neckParent;
    torso.position.y = TORSO_Y;
    this.root.add(torso);

    this.body = new THREE.Mesh(new THREE.CapsuleGeometry(0.2, 0.55, 6, 12), skin);
    this.body.rotation.x = Math.PI / 2;
    this.body.position.set(0, 0.05, 0.25);
    this.body.castShadow = true;
    torso.add(this.body);

    this.neck.position.copy(this.neckPos);
    torso.add(this.neck);
    this.collar.position.set(0, 0.05, 0.5);
    this.collar.rotation.x = -0.3;
    torso.add(this.collar);
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.15, 16, 12), skin);
    head.position.set(0, 0.12, 0.16);
    head.castShadow = true;
    this.neck.add(head);
    this.hat.position.set(0, 0.13, -0.02);
    head.add(this.hat);
    this.mouth.position.set(0, -0.07, 0.3);
    head.add(this.mouth);
    const snout = new THREE.Mesh(new THREE.CapsuleGeometry(0.07, 0.12, 4, 10), dark);
    snout.rotation.x = Math.PI / 2;
    snout.position.set(0, -0.03, 0.18);
    head.add(snout);
    const noseTip = new THREE.Mesh(new THREE.SphereGeometry(0.035, 10, 8), nose);
    noseTip.position.set(0, 0.0, 0.31);
    head.add(noseTip);
    for (const s of [-1, 1]) {
      const eye = new THREE.Group();
      eye.position.set(0.07 * s, 0.05, 0.12);
      const ball = new THREE.Mesh(new THREE.SphereGeometry(0.03, 12, 10), wet);
      eye.add(ball);
      const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.013, 8, 8), pupilMat);
      pupil.position.z = 0.024;
      eye.add(pupil);
      head.add(eye);
      this.eyeGroups.push(eye);

      const drop = profile.earStyle === 'drop';
      const earAnchor = new THREE.Object3D();
      earAnchor.position.set(drop ? 0.12 * s : 0.09 * s, drop ? 0.07 : 0.12, -0.03);
      head.add(earAnchor);
      this.ears.push(
        new SpringChain(earAnchor, scene, {
          segments: 4,
          length: drop ? 0.22 : 0.14,
          radius: drop ? 0.05 : 0.038,
          color: profile.ears,
          restDir: drop ? new THREE.Vector3(0.9 * s, -0.25, -0.15) : new THREE.Vector3(0.35 * s, 1, -0.1),
          droop: drop ? 1.6 : 0.9,
          stiffness: drop ? 160 : 420,
          damping: drop ? 8 : 10,
          gravity: drop ? 3 : 1.5,
          taper: drop ? 0.25 : 0.5,
        }),
      );
    }

    const legPositions: [number, number][] = [
      [-0.12, 0.25],
      [0.12, 0.25],
      [-0.12, -0.25],
      [0.12, -0.25],
    ];
    for (const [x, z] of legPositions) {
      const pivot = new THREE.Group();
      pivot.position.set(x, HIP_Y, z);
      const leg = new THREE.Mesh(new THREE.CapsuleGeometry(0.045, 0.36, 4, 8), skin);
      leg.position.y = -LEG_LEN / 2;
      leg.castShadow = true;
      pivot.add(leg);
      this.root.add(pivot);
      this.legPivots.push(pivot);
      this.legMeshes.push(leg);
    }

    const tailAnchor = new THREE.Object3D();
    tailAnchor.position.set(0, 0.13, -0.17);
    torso.add(tailAnchor);
    const curl = profile.tailStyle === 'curl';
    this.tail = new SpringChain(tailAnchor, scene, {
      segments: curl ? 6 : 4,
      length: curl ? 0.38 : 0.2,
      radius: curl ? 0.04 : 0.035,
      color: profile.coat,
      restDir: curl ? new THREE.Vector3(0, 0.6, -1) : new THREE.Vector3(0, 1, -0.35),
      stiffness: curl ? 180 : 320,
      damping: curl ? 6 : 8,
      gravity: curl ? 2.5 : 1.5,
      taper: 0.6,
    });

    scene.add(this.root);
  }

  legs(feet: FootPose[], sit: number, lie: number, shakeLift: number, kick: number): void {
    for (let i = 0; i < 4; i++) {
      const f = feet[i];
      const rot = -Math.asin(THREE.MathUtils.clamp(f.z / LEG_LEN, -0.9, 0.9));
      const reach = (HIP_Y - f.lift) / Math.max(0.3, Math.cos(rot));
      let y = HIP_Y;
      let r = rot;
      let k = reach / LEG_LEN;
      const sp = SIT_LEGS[i];
      const lp = LIE_LEGS[i];
      y += (sp.y - y) * sit;
      r += (sp.rot - r) * sit;
      k += (sp.k - k) * sit;
      y += (lp.y - y) * lie;
      r += (lp.rot - r) * lie;
      k += (lp.k - k) * lie;
      if (i === 1 && shakeLift > 0) {
        r = -1.3 * shakeLift;
        k = 1 - 0.35 * shakeLift;
      }
      if (i === 2) r += Math.max(0, kick) * 0.9;
      if (i === 3) r += Math.max(0, -kick) * 0.9;
      const pivot = this.legPivots[i];
      pivot.position.y = y;
      pivot.rotation.x = r;
      const m = this.legMeshes[i];
      m.scale.y = k;
      m.position.y = (-LEG_LEN / 2) * k;
    }
  }

  torso(bob: number, pitch: number, sit: number, lie: number): void {
    let ty = TORSO_Y + bob;
    let tp = pitch;
    ty += (SIT_TORSO.y - ty) * sit;
    tp += (SIT_TORSO.pitch - tp) * sit;
    ty += (LIE_TORSO.y - ty) * lie;
    tp += (LIE_TORSO.pitch - tp) * lie;
    this.neckParent.position.y = ty;
    this.neckParent.rotation.x = tp;
  }

  breathe(b: number): void {
    this.body.scale.set(1 + b * 0.02, 1, 1 + b * 0.015);
  }

  look(q: THREE.Quaternion): void {
    this.neck.quaternion.copy(q);
  }

  eyes(yaw: number, pitch: number, lid: number): void {
    for (const eye of this.eyeGroups) {
      eye.rotation.set(pitch, yaw, 0, 'YXZ');
      eye.scale.y = lid;
    }
  }

  setCoat(dirt: number, wet = 0): void {
    this.skin.color.copy(this.coatBase).lerp(new THREE.Color(0x6b5a44), dirt * 0.55).multiplyScalar(1 - 0.35 * wet);
    this.skin.roughness = Math.min(1, 0.9 + dirt * 0.1) - 0.55 * wet;
  }

  finish(dt: number): void {
    this.root.updateMatrixWorld(true);
    this.tail.update(dt);
    for (const e of this.ears) e.update(dt);
  }

  dispose(): void {
    this.scene.remove(this.root);
    this.tail.dispose();
    for (const e of this.ears) e.dispose();
  }
}
